import * as api from 'app/shared/api';
import { ThunkDispatch } from 'redux-thunk';
import {Action, ItemType, AgentPricingTab} from 'app/pages/AgentPricing/actionTypes';
import {
    changeRateFinderCriteria,
    rateFinderQueryRunning,
    rateFinderQueryResult,
    startSavingRates,
    ratesSaveSuccessful,
    ratesSaveError,
    ratesValidationError,
    changeTab,
    startFetchPublishedVersions,
    fetchPublishedVersionsSuccessful,
    fetchPublishedVersionsError
} from "app/pages/AgentPricing/actions";
import { getRateFinderCriteria } from "app/pages/AgentPricing/modules/RateFinderWidget";
import { AgentCommissionRateQueryDto, RateQueryItemType, AgentCommissionRateQueryStatus } from "app/shared/model/AgentCommissionDto";
import { getRatesTable, getSaveAgentCommissionDto } from 'app/pages/AgentPricing/modules/RatesTableWidget/selectors';
import { getAgentId, getSchools, getCountries } from 'app/pages/AgentPricing/modules/RefData';
import { getCurrentChangeNumber } from 'app/pages/AgentPricing/modules/SaveStatus';
import { getSelectedTab } from 'app/pages/AgentPricing/modules/TabMenuBar';
import { getSelectedDivisionId } from "app/pages/AgentPricing/utils/division";
import { getSchoolKey, toSchoolScopeIdFromSchool } from "app/pages/AgentPricing/utils/schoolIds";
import { validateEndDate } from "app/pages/AgentPricing/utils/validateEndDate";

function resolveSchoolKeyFromMaybeCode(schools: any[], schoolKeyOrCode?: string): string | undefined {
    if (schoolKeyOrCode == null) return undefined;

    if (schools.some(s => getSchoolKey(s) === schoolKeyOrCode)) return schoolKeyOrCode;

    const byCode = schools.find(s => s.code === schoolKeyOrCode);
    if (byCode) return getSchoolKey(byCode);

    return schoolKeyOrCode;
}

export function findRateCommand(versionCode: string, itemType?: ItemType, itemId?: string, schoolKey?: string) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        const state = getState();
        const schools = getSchools(state);

        const normalizedSchoolKey = resolveSchoolKeyFromMaybeCode(schools, schoolKey);
        const currentCriteria = getRateFinderCriteria(state);
        const currentSchoolKey = resolveSchoolKeyFromMaybeCode(schools, currentCriteria.schoolId);

        if (currentCriteria.itemType === itemType &&
            currentCriteria.itemId === itemId &&
            currentSchoolKey === normalizedSchoolKey
        ) {
            return;
        }

        dispatch(changeRateFinderCriteria(itemType, itemId, normalizedSchoolKey));

        if (itemId !== undefined || normalizedSchoolKey !== undefined) {
            const divisionId = getSelectedDivisionId();
            if (!divisionId) {
                dispatch(rateFinderQueryResult(undefined as any, { status: AgentCommissionRateQueryStatus.Errored } as any));
                return;
            }

            dispatch(rateFinderQueryRunning());

            const school = normalizedSchoolKey
                ? schools.find(s => getSchoolKey(s) === normalizedSchoolKey)
                : undefined;

            const schoolIdForApi = school ? toSchoolScopeIdFromSchool(school) : undefined;

            const request = getAgentCommissionRateQuery(versionCode, itemType, itemId, schoolIdForApi);

            api.findRate(divisionId, request)
                .then(result => dispatch(rateFinderQueryResult(request, result)))
                .catch(() => dispatch(rateFinderQueryResult(request, { status: AgentCommissionRateQueryStatus.Errored } as any)));
        }
    };
}

export function changeTabCommand(tab: AgentPricingTab) {
    return function(dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        const state = getState();        

        if (getSelectedTab(state) === tab) 
            return; 

        dispatch(changeTab(tab)); 
        if (tab === AgentPricingTab.VersionHistory) {
            dispatch(startFetchPublishedVersions()); 

            const divisionId = getSelectedDivisionId();
            if (!divisionId) {
                dispatch(fetchPublishedVersionsError());
                return;
            }

            const agentId = getAgentId(state);
            const schools = getSchools(state);
            const countries = getCountries(state);

            api.getPublishedAgentCommission(agentId, divisionId)
                .then(results => dispatch(fetchPublishedVersionsSuccessful(results, countries, schools)))
                .catch(() => dispatch(fetchPublishedVersionsError()));
        }
    };
}

export function saveAgentCommissionRates() {
    return function (dispatch: ThunkDispatch<any, any, Action>, getState: () => any) {
        const state = getState();
        const divisionId = getSelectedDivisionId();
        const changeNumber = getCurrentChangeNumber(state);

        if (!divisionId) {
            dispatch(ratesSaveError(changeNumber));
            return;
        }

        const table = getRatesTable(state);
        const endDateError = validateEndDate(
            table.dates.startDate,
            table.dates.endDate,
            table.status
        );
        if (endDateError) {
            dispatch(ratesValidationError(endDateError));
            return;
        }

        const dto = getSaveAgentCommissionDto(state);
        const id = getAgentId(state);

        dispatch(startSavingRates(changeNumber));

        api.putAgentCommissionRates(id, divisionId, dto)
            .then(() => dispatch(ratesSaveSuccessful(changeNumber)))
            .catch(() => dispatch(ratesSaveError(changeNumber)));
    };
}

function getAgentCommissionRateQuery(
    versionCode: string,
    itemType?: ItemType,
    itemId?: string,
    schoolId?: string
): AgentCommissionRateQueryDto {
    let queryItemType: RateQueryItemType | undefined = undefined;

    const hasItemId = itemId != null && String(itemId).trim() !== "";

    if (hasItemId !== undefined && itemType !== undefined) {
        switch (itemType) {
            case ItemType.Fee:
                queryItemType = RateQueryItemType.fee;
                break;
            default:
                queryItemType = RateQueryItemType.product;
                break;
        }
    }

    return {
        versionCode,
        schoolId,
        itemId: hasItemId ? String(itemId) : undefined,
        itemType: queryItemType
    };
}
